/**
 * Resolves the Smart Allotment REST base URL for browser and SSR/build contexts.
 *
 * Multi-tenant: prefer same-origin `/api/...` (reverse proxy), an explicit
 * `REACT_APP_API_URL`, or `REACT_APP_API_ORIGIN_TEMPLATE` (scribeinai or amtranscribe `{tenant}` hosts).
 * See `.env.example` for env vars.
 */

export const API_PATH = "/api/SmartAllotment";

/** Default production API when no same-origin `/api` proxy and no explicit REACT_APP_API_URL. */
const DEFAULT_GLOBAL_SMART_ALLOTMENT_API = "https://api.amtranscribe.com/api/SmartAllotment";

/** Legacy static SPAs with no same-origin `/api` — map misconfigured same-origin to global API. */
const LEGACY_STATIC_SPA_HOSTS_SCRIBEINAI = ["trans.scribeinai.com", "doctor.scribeinai.com", "admin.scribeinai.com"];
const LEGACY_STATIC_SPA_HOSTS_AMTRANSCRIBE = ["trans.amtranscribe.com", "doctor.amtranscribe.com", "admin.amtranscribe.com"];
const LEGACY_STATIC_SPA_HOSTS = [...LEGACY_STATIC_SPA_HOSTS_SCRIBEINAI, ...LEGACY_STATIC_SPA_HOSTS_AMTRANSCRIBE];

const RESERVED_TENANT_LABELS = new Set(["www", "api", "app", "trans", "doctor", "admin"]);

function normalizeHost(hostname) {
  return (hostname || "").toLowerCase().replace(/^www\./, "");
}

function isLocalHost(hostname) {
  const h = normalizeHost(hostname);
  return h === "localhost" || h === "127.0.0.1" || h === "[::1]";
}

/**
 * First label for `*.scribeinai.com` tenant sites, e.g. clinic.scribeinai.com → clinic.
 * Returns null if the host should not use `{tenant}` substitution.
 */
export function tenantLabelFromScribeinaiHost(hostname) {
  const host = normalizeHost(hostname);
  if (!host.endsWith(".scribeinai.com") || host === "scribeinai.com") return null;
  if (host.startsWith("api.")) return null;
  const first = host.split(".")[0];
  if (!first || RESERVED_TENANT_LABELS.has(first)) return null;
  return first;
}

/**
 * First label for `*.amtranscribe.com` tenant sites (parallel to scribeinai).
 */
export function tenantLabelFromAmtranscribeHost(hostname) {
  const host = normalizeHost(hostname);
  if (!host.endsWith(".amtranscribe.com") || host === "amtranscribe.com") return null;
  if (host === "api.amtranscribe.com") return null;
  if (host.startsWith("api.")) return null;
  const first = host.split(".")[0];
  if (!first || RESERVED_TENANT_LABELS.has(first)) return null;
  return first;
}

/** Tenant subdomain for `REACT_APP_API_ORIGIN_TEMPLATE` on either brand domain. */
export function tenantLabelFromMultiTenantHost(hostname) {
  return tenantLabelFromScribeinaiHost(hostname) || tenantLabelFromAmtranscribeHost(hostname);
}

function readEnv(key) {
  if (typeof process === "undefined" || !process.env) return "";
  return process.env[key] || "";
}

/**
 * @returns {string} API base, no trailing slash
 */
export function resolveSmartAllotmentApiUrl() {
  const trimmedEnv = readEnv("REACT_APP_API_URL").trim();

  let rawApiUrl = trimmedEnv || API_PATH;

  if (rawApiUrl.startsWith("http") && !rawApiUrl.includes(API_PATH)) {
    rawApiUrl = rawApiUrl.replace(/\/+$/, "") + API_PATH;
  }

  if (typeof window === "undefined") {
    return rawApiUrl.replace(/\/+$/, "");
  }

  const pageOrigin = window.location.origin;
  const host = normalizeHost(window.location.hostname);

  let apiOrigin = pageOrigin;
  try {
    const s = String(rawApiUrl);
    if (s.startsWith("http")) {
      apiOrigin = new URL(s).origin;
    }
  } catch {
    apiOrigin = pageOrigin;
  }

  const envIsAbsoluteHttp = trimmedEnv.startsWith("http");

  // Dev: relative `/api` → webpack dev server proxy (setupProxy.js)
  if (isLocalHost(host) && !envIsAbsoluteHttp) {
    return rawApiUrl.replace(/\/+$/, "");
  }

  // Optional per-tenant API origin, e.g. REACT_APP_API_ORIGIN_TEMPLATE=https://api.{tenant}.scribeinai.com
  const template = readEnv("REACT_APP_API_ORIGIN_TEMPLATE").trim();
  if (!envIsAbsoluteHttp && template) {
    const tenant = tenantLabelFromMultiTenantHost(host);
    if (tenant) {
      const originPart = template.replace(/\{tenant\}/gi, tenant).replace(/\/+$/, "");
      if (originPart.startsWith("http")) {
        rawApiUrl = originPart + API_PATH;
        return rawApiUrl.replace(/\/+$/, "");
      }
    }
  }

  // Legacy trans/doctor/admin portal hosts (both brands → global API)
  if (LEGACY_STATIC_SPA_HOSTS.includes(host)) {
    if (apiOrigin === pageOrigin || !String(rawApiUrl).startsWith("http")) {
      rawApiUrl = DEFAULT_GLOBAL_SMART_ALLOTMENT_API;
    }
    return rawApiUrl.replace(/\/+$/, "");
  }

  // Explicit absolute API URL from build env — highest priority for non-legacy hosts
  if (envIsAbsoluteHttp) {
    return rawApiUrl.replace(/\/+$/, "");
  }

  // Static preview hosts (no `/api` on the CDN origin)
  const isPreviewHost =
    host.endsWith(".amplifyapp.com") || host.endsWith(".vercel.app") || host.endsWith(".netlify.app");
  if (isPreviewHost) {
    return DEFAULT_GLOBAL_SMART_ALLOTMENT_API.replace(/\/+$/, "");
  }

  // Opt-in: map every *.scribeinai.com SPA to the global API (wrong for per-clinic tenants using tenant APIs).
  const legacy = readEnv("REACT_APP_LEGACY_SCRIBEINAI_GLOBAL_API").trim().toLowerCase();
  if (legacy === "true" || legacy === "1" || legacy === "yes") {
    if (host.endsWith(".scribeinai.com") && !host.startsWith("api.")) {
      return DEFAULT_GLOBAL_SMART_ALLOTMENT_API.replace(/\/+$/, "");
    }
  }

  const legacyAmtranscribe = readEnv("REACT_APP_LEGACY_AMTRANSCRIBE_GLOBAL_API").trim().toLowerCase();
  if (legacyAmtranscribe === "true" || legacyAmtranscribe === "1" || legacyAmtranscribe === "yes") {
    if (host.endsWith(".amtranscribe.com") && host !== "api.amtranscribe.com" && !host.startsWith("api.")) {
      return DEFAULT_GLOBAL_SMART_ALLOTMENT_API.replace(/\/+$/, "");
    }
  }

  const extraHosts = readEnv("REACT_APP_GLOBAL_API_FALLBACK_HOSTS")
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  if (extraHosts.includes(host)) {
    return DEFAULT_GLOBAL_SMART_ALLOTMENT_API.replace(/\/+$/, "");
  }

  const extraHostsAmtranscribe = readEnv("REACT_APP_GLOBAL_AMTRANSCRIBE_API_FALLBACK_HOSTS")
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  if (extraHostsAmtranscribe.includes(host)) {
    return DEFAULT_GLOBAL_SMART_ALLOTMENT_API.replace(/\/+$/, "");
  }

  return rawApiUrl.replace(/\/+$/, "");
}
